// The owner console (round 32) — every company, read-only.
//
// Nathan's screen, and nobody else's. Round 25 deferred a web admin panel
// because it is "a door past tenant isolation by definition"; this is the
// narrowest version that answers the question the deferral admitted was real —
// how is the business going, from something other than a shell script.
//
// WHAT IT DELIBERATELY IS NOT: a place to DO anything. There is no button on
// this page and there is not meant to be one. Extending a trial or freezing an
// account is a write into somebody else's company, and each of those wants its
// own audit trail and its own thinking about what it does mid-billing-run.
// `ops/customers.sh` and a deliberate command on the box remain how those
// happen.
//
// The server refuses this data to anyone without the platform flag AND a
// second factor, so this page's only job is to draw what it is given. It shows
// counts and money — never an employee, a client or a document.
import { useCallback, useEffect, useState } from 'react';
import { ApiError, api, type PlatformCompany, type PlatformReadout } from '../api';
import { Banner } from '../ui';
import { PageHeader, StatusBadge } from '../components/ui';
import { CardList, RowCard } from '../components/ui/RowCard';
import { SortHeader } from '../components/ui/SortHeader';
import { sortRows, useSort } from '../lib/sortRows';

type SortKey =
  | 'name'
  | 'status'
  | 'seats'
  | 'latest'
  | 'thisMonth'
  | 'outstanding'
  | 'storage'
  | 'quotes'
  | 'signedUp';

/** '$17.50' — money is integer cents everywhere, including here. */
function money(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function storage(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${Math.round(bytes / (1024 * 1024))} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

const seatLine = (company: PlatformCompany) =>
  `${company.seats.admin}a / ${company.seats.manager}m / ${company.seats.field}f` +
  (company.invited > 0 ? ` · ${company.invited} invited` : '');

/**
 * Is quoting being used (round 43)? Counts only: how many real quotes and how
 * many became jobs. Never a value, a client or a title.
 */
const quoteLine = (company: PlatformCompany) =>
  `${company.quotes.created} quotes · ${company.quotes.converted} to jobs`;

/** Red for the one that costs money, amber for the clock, plain for the rest. */
function statusKind(company: PlatformCompany) {
  if (company.dunning) return 'unavailable' as const;
  if (company.status === 'read_only' || company.status === 'cancelled') {
    return 'unavailable' as const;
  }
  if (company.status === 'trialing') return 'today' as const;
  return 'available' as const;
}

export function PlatformPage() {
  const [readout, setReadout] = useState<PlatformReadout | null>(null);
  const [error, setError] = useState<string | null>(null);
  // This list had no order at all — not here and not on the server, so it
  // arrived in whatever order Postgres felt like. Newest signup first is the
  // one an owner opening this page wants.
  const { sortKey, sortDir, onSort } = useSort<SortKey>('signedUp', 'desc');

  const load = useCallback(async () => {
    try {
      setReadout(await api.platformCompanies());
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not read the companies.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (!readout) {
    return (
      <section className="legacy-page">
        <PageHeader title="Owner console" />
        {error ? <Banner kind="error">{error}</Banner> : <p className="muted">Loading…</p>}
      </section>
    );
  }

  const { companies } = readout;
  // Not memoised: this runs after an early return, so a hook cannot go here,
  // and an owner console holds a handful of rows rather than thousands.
  const visible = sortRows(
    companies,
    (company) => {
      switch (sortKey) {
        case 'name':
          return company.name;
        case 'status':
          return company.dunning ? 'Payment failing' : company.status;
        case 'seats':
          return company.seats.admin + company.seats.manager + company.seats.field;
        case 'latest':
          return company.latest?.number ?? null;
        // The four below are numbers and dates, and every one of them would
        // sort wrongly as the formatted string beside it — "$175.00" before
        // "$17.50", "9.4 MB" after "812 KB".
        case 'thisMonth':
          return company.invoicedThisMonthCents;
        case 'outstanding':
          return company.outstandingCents;
        case 'storage':
          return company.storageUsedBytes;
        case 'quotes':
          return company.quotes.created;
        case 'signedUp':
          return new Date(company.createdAt);
      }
    },
    sortDir
  );
  const totalSeats = companies.reduce(
    (sum, c) => sum + c.seats.admin + c.seats.manager + c.seats.field,
    0
  );
  const invoicedThisMonth = companies.reduce((sum, c) => sum + c.invoicedThisMonthCents, 0);
  const outstanding = companies.reduce((sum, c) => sum + c.outstandingCents, 0);
  const behind = companies.filter((c) => c.dunning);

  return (
    <section className="legacy-page">
      <PageHeader title="Owner console" />

      {error && <Banner kind="error">{error}</Banner>}
      {readout.unreadable > 0 && (
        <Banner kind="error">
          {readout.unreadable} {readout.unreadable === 1 ? 'company' : 'companies'} could not be
          read. Each one has been reported; the numbers below are missing them.
        </Banner>
      )}

      <section className="panel">
        <div className="row mb-3">
          <h2 className="!mb-0">Today</h2>
        </div>
        {/* `.details` is the two-column definition grid the rest of the app
            uses, and it wants FLAT dt/dd children — wrapping each pair in a
            div drops them out of the grid and they stack. */}
        <dl className="details">
          <dt>Companies</dt>
          <dd>{companies.length}</dd>
          <dt>Seats</dt>
          <dd>{totalSeats}</dd>
          <dt>Invoiced this month</dt>
          <dd>{money(invoicedThisMonth)}</dd>
          <dt>Outstanding</dt>
          <dd>{money(outstanding)}</dd>
        </dl>
        <p className="hint">
          Months are counted on {readout.timezone.replace('_', ' ')} time: your calendar, not
          each company's. Read {new Date(readout.readAt).toLocaleString('en-AU')}.
        </p>
      </section>

      {behind.length > 0 && (
        <section className="panel attention">
          <div className="row mb-3">
            <h2 className="!mb-0">Behind on payment</h2>
          </div>
          <ul className="space-y-1">
            {behind.map((company) => (
              <li key={company.id}>
                <b>{company.name}</b> · {money(company.outstandingCents)} outstanding
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="panel">
        <div className="row mb-3">
          <h2 className="!mb-0">Companies</h2>
        </div>

        {companies.length === 0 ? (
          <p className="muted">No companies yet. Signups are the switch, not this page.</p>
        ) : (
          <>
            {/* Cards on phones, a table on desktop — the same rule every list
                page in the product follows. */}
            <CardList>
              {visible.map((company) => (
                <RowCard
                  key={company.id}
                  title={company.name}
                  badges={
                    <>
                      <StatusBadge
                        kind="today"
                        status={statusKind(company)}
                        label={company.dunning ? 'Payment failing' : company.status}
                      />
                      {company.founding && <span className="pill">Founding</span>}
                    </>
                  }
                >
                  <div>{seatLine(company)}</div>
                  <div>
                    {company.latest
                      ? `${company.latest.number ?? 'draft'} · ${money(company.latest.totalCents)} · ${company.latest.status}`
                      : 'No invoices yet'}
                  </div>
                  <div>
                    {money(company.invoicedThisMonthCents)} this month ·{' '}
                    {storage(company.storageUsedBytes)} stored
                  </div>
                  <div>{quoteLine(company)}</div>
                </RowCard>
              ))}
            </CardList>

            <div className="-mx-6 overflow-x-auto border-y border-gray-200 max-sm:hidden">
              <table className="data-table">
                <thead>
                  <tr>
                    <SortHeader label="Company" sortKey="name" current={sortKey} dir={sortDir} onSort={onSort} />
                    <SortHeader label="Status" sortKey="status" current={sortKey} dir={sortDir} onSort={onSort} />
                    <SortHeader label="Seats" sortKey="seats" current={sortKey} dir={sortDir} onSort={onSort} />
                    <SortHeader label="Latest invoice" sortKey="latest" current={sortKey} dir={sortDir} onSort={onSort} />
                    <SortHeader label="This month" sortKey="thisMonth" current={sortKey} dir={sortDir} onSort={onSort} className="table-num" />
                    <SortHeader label="Outstanding" sortKey="outstanding" current={sortKey} dir={sortDir} onSort={onSort} className="table-num" />
                    <SortHeader label="Storage" sortKey="storage" current={sortKey} dir={sortDir} onSort={onSort} className="table-num" />
                    <SortHeader label="Quotes" sortKey="quotes" current={sortKey} dir={sortDir} onSort={onSort} />
                    <SortHeader label="Signed up" sortKey="signedUp" current={sortKey} dir={sortDir} onSort={onSort} />
                  </tr>
                </thead>
                <tbody>
                  {visible.map((company) => (
                    <tr key={company.id}>
                      <td>
                        {company.name}
                        {company.founding && <span className="pill ml-1">Founding</span>}
                      </td>
                      <td>
                        <StatusBadge
                          kind="today"
                          status={statusKind(company)}
                          label={company.dunning ? 'Payment failing' : company.status}
                        />
                      </td>
                      <td className="table-muted whitespace-nowrap">{seatLine(company)}</td>
                      <td className="table-muted whitespace-nowrap">
                        {company.latest
                          ? `${company.latest.number ?? 'draft'} · ${money(company.latest.totalCents)} · ${company.latest.status}`
                          : '—'}
                      </td>
                      {/* Money reads down the column, so it is tabular and
                          right aligned — "$175.00" under "$17.50" only
                          compares if the digits line up. */}
                      <td className="table-muted table-num whitespace-nowrap">
                        {money(company.invoicedThisMonthCents)}
                      </td>
                      <td className="table-muted table-num whitespace-nowrap">
                        {money(company.outstandingCents)}
                      </td>
                      <td className="table-muted table-num whitespace-nowrap">
                        {storage(company.storageUsedBytes)}
                      </td>
                      <td className="table-muted whitespace-nowrap">{quoteLine(company)}</td>
                      <td className="table-muted whitespace-nowrap">
                        {new Date(company.createdAt).toLocaleDateString('en-AU')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <p className="hint">
          Read-only, on purpose. Anything that changes a customer's account is a command on the
          box; see <code>ops/customers.sh</code>.
        </p>
      </section>
    </section>
  );
}
